import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore, useHasHydrated, useIsAuthenticated } from '@/stores/authStore';
import { AuthPageLayout } from '@components/auth/AuthPageLayout';
import { GoogleAuthSection } from '@components/auth/GoogleSignInButton';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const isLoading = useAuthStore((state) => state.isLoading);
  const authError = useAuthStore((state) => state.error);
  const clearError = useAuthStore((state) => state.clearError);
  const isAuthenticated = useIsAuthenticated();
  const hasHydrated = useHasHydrated();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [needsOTP, setNeedsOTP] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ email?: string; password?: string; otp?: string }>({});

  useEffect(() => {
    clearError();
  }, [clearError]);

  const validateCredentials = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (needsOTP) {
      if (!otpCode || otpCode.length !== 6) {
        setErrors({ otp: 'Enter the 6-digit code from your email' });
        return;
      }
      try {
        const result = await login(email, password, otpCode);
        if (result?.requires_otp) {
          toast.error('Still waiting for a verification code');
          return;
        }
        toast.success('Welcome back!');
        navigate('/home');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Invalid verification code');
      }
      return;
    }

    if (!validateCredentials()) return;

    try {
      const result = await login(email, password);
      if (result?.requires_otp) {
        setNeedsOTP(true);
        setDevCode(result.dev_code ?? null);
        toast.message('Check your email for a verification code');
        return;
      }
      toast.success('Welcome back!');
      navigate('/home');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Sign in failed');
    }
  };

  const handleBackToCredentials = () => {
    setNeedsOTP(false);
    setOtpCode('');
    setDevCode(null);
    setErrors({});
    clearError();
  };

  if (hasHydrated && isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return (
    <AuthPageLayout
      title={needsOTP ? 'Check your email' : 'Welcome back'}
      subtitle={
        needsOTP
          ? `We sent a 6-digit code to ${email}. Enter it below to finish signing in.`
          : 'Sign in to organize tabs, run AI skills, and turn open pages into working apps.'
      }
      alternatePrompt="New to Khiimori?"
      alternateLinkTo="/register"
      alternateLinkLabel="Create an account"
      legalAction="signing in"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {authError && (
          <p
            className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300"
            role="alert"
          >
            {authError}
          </p>
        )}

        {!needsOTP ? (
          <>
            <Input
              label="Work email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (authError) clearError();
              }}
              error={errors.email}
              autoComplete="email"
              aria-required="true"
            />

            <Input
              label="Password"
              type="password"
              placeholder="Your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (authError) clearError();
              }}
              error={errors.password}
              autoComplete="current-password"
              aria-required="true"
              passwordToggle
            />

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              isLoading={isLoading}
              aria-label="Sign in to your account"
            >
              Sign in
            </Button>

            <GoogleAuthSection />
          </>
        ) : (
          <>
            <Input
              label="Verification code"
              type="text"
              inputMode="numeric"
              placeholder="123456"
              value={otpCode}
              onChange={(e) => {
                setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                if (errors.otp) setErrors({});
                if (authError) clearError();
              }}
              error={errors.otp}
              autoComplete="one-time-code"
              maxLength={6}
              aria-required="true"
            />

            {devCode && (
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                Dev mode (SMTP off): use code <span className="font-mono font-semibold">{devCode}</span>
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              isLoading={isLoading}
              disabled={otpCode.length !== 6}
              aria-label="Verify sign-in code"
            >
              Verify and sign in
            </Button>

            <button
              type="button"
              onClick={handleBackToCredentials}
              className="w-full text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              Back to sign in
            </button>
          </>
        )}
      </form>
    </AuthPageLayout>
  );
}
